import { Button } from '@/components/ui'
import { Pagination } from '@/components/ui/pagination'
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useChainRuns, useChains, useDebounce, useNowTick, usePagination } from '@/hooks'
import type { ChainRunFilters as ChainRunFiltersType, ChainRunStatus, ChainRunTriggerSource } from '@/types'
import { isActiveChainRunStatus } from '@/types'
import { AlertCircle, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ChainRunFilters } from './ChainRunFilters'
import { ChainRunRow } from './ChainRunRow'
import { chainRunStatusFilterOptions } from './chainRunStatusConfig'

function statusFromUrl(value: string | null): ChainRunStatus | '' {
  return chainRunStatusFilterOptions.find(option => option.value && option.value === value)?.value ?? ''
}

function triggerFromUrl(value: string | null): ChainRunTriggerSource | '' {
  return value === 'AzureDevOpsIngestion' ? value : ''
}

export function ChainRunsTab() {
  const { projectId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { resetPage, ...pagination } = usePagination()

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<ChainRunStatus | ''>(() => statusFromUrl(searchParams.get('status')))
  const [chainFilter, setChainFilter] = useState('')
  const [triggerFilter, setTriggerFilter] = useState<ChainRunTriggerSource | ''>(() => triggerFromUrl(searchParams.get('trigger')))

  const debouncedSearch = useDebounce(searchTerm)

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const writeUrlFilter = useCallback((key: 'status' | 'trigger', value: string) => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous)
      if (value) next.set(key, value)
      else next.delete(key)
      return next
    }, { replace: true })
  }, [setSearchParams])

  const handleStatusChange = useCallback((value: ChainRunStatus | '') => {
    setStatusFilter(value)
    writeUrlFilter('status', value)
  }, [writeUrlFilter])

  const clearTriggerFilter = useCallback(() => {
    setTriggerFilter('')
    writeUrlFilter('trigger', '')
  }, [writeUrlFilter])

  const handleChainChange = useCallback((value: string) => {
    setChainFilter(value)
  }, [])

  useEffect(() => {
    resetPage()
  }, [debouncedSearch, statusFilter, chainFilter, triggerFilter, resetPage])

  const filters: ChainRunFiltersType = useMemo(() => ({
    search: debouncedSearch.trim(),
    status: statusFilter,
    chainDefinitionId: chainFilter === '_all' ? '' : chainFilter,
    triggerSource: triggerFilter,
  }), [debouncedSearch, statusFilter, chainFilter, triggerFilter])

  const { data, isLoading, isError, refetch } = useChainRuns(projectId!, pagination.paginationParams, filters)
  const chainRuns = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  useNowTick(chainRuns.some(r => isActiveChainRunStatus(r.status)))

  const { data: chainsData } = useChains(projectId!)

  const chains = useMemo(
    () => (chainsData?.items ?? []).map(c => ({ id: c.id, displayName: c.displayName })),
    [chainsData],
  )

  return (
    <div className="space-y-6">
      <ChainRunFilters
        search={searchTerm}
        status={statusFilter}
        chainDefinitionId={chainFilter}
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        onChainChange={handleChainChange}
        chains={chains}
        onClearAll={() => {
          setSearchTerm('')
          setStatusFilter('')
          setChainFilter('_all')
          setTriggerFilter('')
          setSearchParams(previous => {
            const next = new URLSearchParams(previous)
            next.delete('status')
            next.delete('trigger')
            return next
          }, { replace: true })
        }}
      />

      {triggerFilter === 'AzureDevOpsIngestion' && (
        <div className="flex items-center gap-2 text-sm" data-testid="chain-runs-trigger-filter">
          <span className="inline-flex items-center gap-1 rounded-full border bg-muted px-3 py-1">
            Automatically started only
            <button
              type="button"
              aria-label="Show all chain runs, not only automatically started ones"
              onClick={clearTriggerFilter}
              className="rounded-full hover:text-foreground text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3" role="status" aria-live="polite" data-testid="chain-runs-loading">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 rounded-lg border bg-muted animate-pulse" />
          ))}
        </div>
      ) : isError ? (
        <div
          className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16"
          role="alert"
          data-testid="chain-runs-error"
        >
          <AlertCircle className="h-12 w-12 text-destructive" />
          <h3 className="mt-4 text-lg font-semibold">Failed to load chain runs</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Something went wrong while loading your chain runs.
          </p>
          <Button onClick={() => refetch()} variant="outline" className="mt-4">
            Try Again
          </Button>
        </div>
      ) : (
        <>
          {chainRuns.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="chain-runs-empty">
              <p>No chain runs yet</p>
              <p className="mt-1 text-sm">Start a chain from the Chains page to see its runs here.</p>
            </div>
          ) : (
            <div className="rounded-lg border" data-testid="chain-runs-table">
              <Table className="table-auto" aria-label="Chain runs">
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="w-[220px]">Chain</TableHead>
                    <TableHead className="w-[140px]">Linked item</TableHead>
                    <TableHead className="w-[170px]">Status</TableHead>
                    <TableHead className="w-[160px]">Progress</TableHead>
                    <TableHead className="w-[130px]">Started</TableHead>
                    <TableHead className="w-[80px]">Duration</TableHead>
                    <TableHead className="w-[180px]">User</TableHead>
                    <TableHead className="w-[150px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chainRuns.map(run => (
                    <ChainRunRow key={run.id} run={run} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalCount > pagination.pageSize && (
            <Pagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalCount={totalCount}
              pageSizeOptions={pagination.pageSizeOptions}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          )}
        </>
      )}
    </div>
  )
}
